import { Checkbox, Container, Divider, FormHelperText, Grid, MenuItem, Select, TextField } from '@material-ui/core'
import React, { useState, useMemo, useEffect, useContext } from 'react'
import FlexView from 'react-flexview/lib'
import { Controller, useForm } from 'react-hook-form'
import { IS_MVR_ACTIVE, STATES, VIN_EXPLANATIONS, stateWithMotorcycleEndoresments, MOTORCYCLE_ENDORESMENTS } from '../../Constants'
import { SessionContextStore, SESSION_ACTIONS } from '../../context/SessionContext'
import { EventEmitterService, EventKey } from '../../services/EventEmitterService'
import { BackButton } from '../Core/Buttons/BackButton'
import { ContinueButton } from '../Core/Buttons/ContinueButton'
import { ReactComponent as IssuanceSvg } from '../../assets/issuance.svg'
import operatorApi from '../../api/operators.api'
import vehicleApi from '../../api/vehicles.api'
import { awaitWithLoading, capitalizeAllLetters, vinValidation } from '../../services/utils'
import { BuyingInsuranceContextStore } from '../../context/BuyingInsuranceContext'
import { Action } from '../../models/Action'
import { SkyInformationPopover } from '../Core/Popover/SkyPopover'
import { reportGTM } from '../Core/Utilities/GTagManger'
import { isMobile } from 'react-device-detect'
import { mvrUpdate } from './SummarySection/Mvr'
import { UserApi } from '../../api/user.api'
import { generalPhoneNumberRegex } from '../../services/utils'
import SkywatchDropzone from '../Core/SkywatchDropzone'
import { DropzonePlaceHolder } from '../Core/DropzonePlaceHolder'
import { ReactComponent as PdfIcon } from '../../assets/pdfIcon.svg'
import { ReactComponent as TrashIcon } from '../../assets/trash.svg'
import { SessionService } from '../../services/SessionService'

import queryString from 'query-string'
import './Issuance.scss'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'

const AsyncFlag = require('async-flag')

const MotorcycleIssuanceForm = ({ defaultValues = {}, onFormSubmit, index, SAVE_EVENT_KEY, DISPATCHED_EVENT, flag }) => {
  const curVehicleId = defaultValues.id
  const sessionContext = React.useContext(SessionContextStore)
  const curVehicle = sessionContext.user.userVehicles.filter(x => x.id == curVehicleId)[0]
  const [checked, setChecked] = React.useState(false)

  const { errors, getValues, triggerValidation, register, control } = useForm({ defaultValues: defaultValues, mode: 'onChange' })
  const handleCheckboxChange = event => {
    setChecked(event.target.checked)

    if (event.target.checked) {
      reportGTM('', '', { event: 'add-vin-later-trigger', at: window.location.pathname })
    }
  }

  React.useEffect(() => {
    setChecked(curVehicle.vin == '' ? true : false)
  }, [])

  React.useEffect(() => {
    register('id')
    let id = EventEmitterService.subscribe(SAVE_EVENT_KEY, async () => {
      let valid = await triggerValidation()
      if (valid) {
        let updatedVehicle = { ...defaultValues, vin: getValues()['vin'] }
        let res = await vehicleApi.updateVehicle(updatedVehicle)
        if (res.ok) {
          EventEmitterService.dispatch(EventKey.UPDATE_VEHICLE, updatedVehicle)
          flag.set(defaultValues.id)
        } else {
          flag.error(res)
        }
      } else {
        flag.error({ data: 'validation' })
      }
    })

    return function cleanup() {
      EventEmitterService.unsubscribe(SAVE_EVENT_KEY, id)
    }
  }, [])

  return (
    <>
      <TextField
        InputLabelProps={{ className: 'avenir-book' }}
        variant="outlined"
        fullWidth
        inputRef={register({ required: !checked, maxLength: 17, minLength: defaultValues.year <= 1980 ? 0 : 17 })}
        onChange={vinValidation}
        name="vin"
        inputProps={{ style: { textTransform: 'uppercase' } }}
        helperText={errors.vin && (errors.vin.type == 'required' ? 'Required' : 'Vin must contain 17 characters')}
        label={'Vehicle identification number (VIN)'}
        error={!!errors.vin}
      />

      {!sessionContext.newPaymentFlow && (
        <FlexView vAlignContent={'center'}>
          <div className="text-small black">Don't have your VIN number?</div>
          <FlexView vAlignContent={'center'} className="text-overflow-elipsis">
            <Checkbox color="primary" checked={checked} onChange={handleCheckboxChange} inputProps={{ 'aria-label': 'controlled' }} />
            <div className="text-small black">Add it later</div>
          </FlexView>
        </FlexView>
      )}
    </>
  )
}

const PhoneNumberForm = ({ defaultValues = {}, SAVE_EVENT_KEY, flag }) => {
  const sessionContext = React.useContext(SessionContextStore)

  const { errors, getValues, triggerValidation, register, control, watch } = useForm({ defaultValues: defaultValues, mode: 'onChange' })
  const phoneNumberWatch = watch('phone_number', defaultValues.phoneNumber)

  const validatePhoneNumber = phoneNumber => !phoneNumber || generalPhoneNumberRegex.test(phoneNumber)

  React.useEffect(() => {
    register('id')
    let id = EventEmitterService.subscribe(SAVE_EVENT_KEY, async () => {
      let valid = await triggerValidation()
      if (valid) {
        let res = await UserApi.updateUserPhoneNumber(getValues()['phone_number'])
        if (!res.ok) {
          flag.error(res)
          EventEmitterService.dispatch(EventKey.ShowError, res)
        } else {
          flag.set(flag.userId)
          const newData = { ...defaultValues, phoneNumber: getValues()['phone_number'] }
          sessionContext.dispatch(new Action(EventKey.UPDATE_USER_PROFILE, newData))
        }
      } else {
        flag.error({ data: 'validation' })
      }
    })

    return function cleanup() {
      EventEmitterService.unsubscribe(SAVE_EVENT_KEY, id)
    }
  }, [])

  return (
    <>
      <FlexView vAlignContent={'center'} className="text-body margin-bottom-basic">
        <div className="bold text-small black">Phone number</div>
        <SkyInformationPopover style={{ fontSize: '0.9rem' }}>
          <div className="text-small">
            {
              'We will use your phone number to remind you to upload the odometer photo per each payment period. Uploading your odometer on time will ensure the most accurate charge based on your recent riding.'
            }
          </div>
        </SkyInformationPopover>
        <div className="text-small black margin-left-3">
          {' '}
          {isMobile ? '(Make sure it is accurate)' : "(Just making sure it's your real phone number)"}
        </div>
      </FlexView>
      <TextField
        InputLabelProps={{ shrink: !!phoneNumberWatch }}
        fullWidth
        variant="outlined"
        defaultValue={phoneNumberWatch}
        label="Phone number"
        name="phone_number"
        inputRef={register({ required: true, validate: validatePhoneNumber })}
        autoComplete="tel"
        error={!!errors.phone_number}
        helperText={errors.phone_number ? 'Invalid phone number' : ''}
        InputLabelProps={{ className: 'avenir-book' }}
        // InputProps={{endAdornment: <InputAdornment position="start"> <OptionalTag /> </InputAdornment>}}
      />
    </>
  )
}

const IssuanceDriverForm = ({ defaultValues = {}, onFormSubmit, index, SAVE_EVENT_KEY, DISPATCHED_EVENT, flag }) => {
  const { errors, getValues, triggerValidation, register, control } = useForm({ defaultValues: defaultValues, mode: 'onChange' })
  const sessionContext = React.useContext(SessionContextStore)
  const offersContext = React.useContext(BuyingInsuranceContextStore)
  const insuredPerson = sessionContext?.user?.userInsuredPersons?.find(per => per.id === defaultValues.id)
  const [motorcycleEndorsement, setMotorcycleEndorsement] = React.useState(insuredPerson?.driver_license_image_url)
  const [motorcycleEndorsementFile, setMotorcycleEndorsementFile] = React.useState(undefined)
  const [motorcycleEndorsementError, setMotorcycleEndorsementError] = React.useState('')
  const showMotorcycleEndoresments = stateWithMotorcycleEndoresments.includes(sessionContext.user.state)

  const inputValidation = e => {
    const validCharactersRegex = /^[0-9a-zA-Z\b]+$/

    if (validCharactersRegex.test(e.target.value)) {
      e.target.value = e.target.value
    } else {
      e.target.value = e.target.value.replaceAll(/[^a-zA-Z0-9]/g, '')
    }
  }

  const handleOnFileSelected = async files => {
    if (files.length === 0) {
      setMotorcycleEndorsementError('Please select smaller file')
      return
    }
    if (files.length > 1) {
      setMotorcycleEndorsementError('Please select only one file')
      return
    }
    setMotorcycleEndorsementError('')
    setMotorcycleEndorsementFile(files)
  }

  React.useEffect(() => {
    register('id')
    let id = EventEmitterService.subscribe(SAVE_EVENT_KEY, async () => {
      let valid = await triggerValidation()
      let validForm =
        valid &&
        (SessionService.isAdmin() ||
          !showMotorcycleEndoresments ||
          motorcycleEndorsementFile !== undefined ||
          motorcycleEndorsement !== undefined)

      setMotorcycleEndorsementError(
        motorcycleEndorsementFile !== undefined || motorcycleEndorsement !== undefined || SessionService.isAdmin()
          ? ''
          : 'Please upload the driver’s motorcycle endorsement'
      )
      if (validForm) {
        let imageUrlRes = undefined
        if (motorcycleEndorsementFile) {
          imageUrlRes = await operatorApi.uploadLicense(defaultValues.id, motorcycleEndorsementFile[0])
          if (!imageUrlRes.ok) {
            setMotorcycleEndorsementError('try other file')
            setMotorcycleEndorsementFile(undefined)
            return undefined
          }
          setMotorcycleEndorsement(imageUrlRes?.data?.image_url)
        }
        let updatedOperator = {
          ...defaultValues,
          driver_license: capitalizeAllLetters(getValues()['driver_license']),
          license_state: getValues()['license_state'],
          driver_license_image_url: imageUrlRes ? imageUrlRes.data?.image_url : motorcycleEndorsement,
        }
        updatedOperator.operates = updatedOperator.user_vehicle_details.map(q => q.id)
        let res = await operatorApi.updateOperator(updatedOperator)
        if (res.ok) {
          if (IS_MVR_ACTIVE) {
            if (res.data) {
              let mvrRes = await mvrUpdate(res.data, offersContext)
              let updateDrivingHistory = mvrRes.releventViolationsList
              updatedOperator = {
                ...updatedOperator,
                insured_person_driving_history: updateDrivingHistory,
                isNewMvrInfo: mvrRes.isNewMvrInfo,
              }
            }
          }
          flag.set(defaultValues.id)
          EventEmitterService.dispatch(EventKey.UPDATE_OPERATOR, updatedOperator)
        } else {
          flag.error(res)
        }
      } else {
        flag.error({ data: 'validation' })
      }
    })

    return function cleanup() {
      EventEmitterService.unsubscribe(SAVE_EVENT_KEY, id)
    }
  }, [motorcycleEndorsementFile, motorcycleEndorsement])

  return (
    <>
      <TextField
        InputLabelProps={{ className: 'avenir-book' }}
        variant="outlined"
        fullWidth
        inputRef={register({ required: true })}
        inputProps={{ style: { textTransform: 'uppercase' } }}
        name="driver_license"
        label={"Driver's license number"}
        onChange={inputValidation}
        error={!!errors.driver_license}
        helperText={!!errors.driver_license && (errors.driver_license.type == 'required' ? 'Required' : "Invalid Driver's License")}
      />

      <Controller
        control={control}
        rules={{ validate: x => x && x != 'default', required: true }}
        name="license_state"
        defaultValue={defaultValues.license_state || 'default'}
        as={
          <Select error={!!errors.license_state} variant="outlined" fullWidth className="margin-top-basic">
            <MenuItem disabled value={'default'}>
              <div className="placeholder-text">Driver's license state</div>
            </MenuItem>
            {Object.keys(STATES).map(t => (
              <MenuItem key={t} value={STATES[t]}>
                {STATES[t]}
              </MenuItem>
            ))}
          </Select>
        }
      />
      {!showMotorcycleEndoresments && <FormHelperText error={!!errors.license_state}>Select license state</FormHelperText>}
      {showMotorcycleEndoresments && (
        <FlexView vAlignContent={'center'} className=" small-margin-top">
          <FormHelperText>Motorcycle Endoresments</FormHelperText>
          <SkyInformationPopover style={{ fontSize: '0.9rem' }}>
            <div className="text-small">{MOTORCYCLE_ENDORESMENTS}</div>
          </SkyInformationPopover>
        </FlexView>
      )}
      {showMotorcycleEndoresments &&
        (!motorcycleEndorsement && !motorcycleEndorsementFile ? (
          <FlexView vAlignContent={'center'}>
            <div className="full-width">
              <SkywatchDropzone
                maxSize={5000000}
                onFileSelected={handleOnFileSelected}
                error={motorcycleEndorsementError}
                accept="image/png, image/jpeg, image/gif"
                placeholder={
                  <DropzonePlaceHolder mainText={'Motorcycle endorsement'} helperText={'Click to upload PNG, JPG or GIF (max. 5 MB)'} />
                }
              />
            </div>
          </FlexView>
        ) : (
          <FlexView className="file-container">
            <div className="file-div flex">
              <PdfIcon />
              <div className="file-name">
                {defaultValues.first_name} {defaultValues.last_name} MC Endorsement
              </div>
            </div>
            <div className="align-content-center trash">
              <TrashIcon
                onClick={() => {
                  setMotorcycleEndorsement(undefined)
                  setMotorcycleEndorsementFile(undefined)
                }}
              />
            </div>
          </FlexView>
        ))}
    </>
  )
}

export const Issuance = () => {
  const sessionContext = React.useContext(SessionContextStore)
  const offersContext = React.useContext(BuyingInsuranceContextStore)
  const insuredPersonsWithFlags = useMemo(() => {
    return sessionContext.user.userInsuredPersons.map(user => {
      return { ...user, flag: new AsyncFlag(user.id) }
    })
  }, [sessionContext.user.userInsuredPersons])
  const insuredPersonsVehiclesWithFlags = useMemo(() => {
    return sessionContext.user.userVehicles.map(vehicle => {
      return { ...vehicle, flag: new AsyncFlag(vehicle.id) }
    })
  }, [sessionContext.user.userVehicles])
  const userPhoneNumberFlag = useMemo(() => {
    return { flag: new AsyncFlag(sessionContext.user.phoneNumber) }
  }, [sessionContext.user.phoneNumber])
  let location = useLocation()
  const { OfferId } = queryString.parse(location.search)

  useEffect(() => {
    if (offersContext.shouldPassIssuance) {
      onContinue()
      offersContext.passIssuance(false)
    }
  }, [offersContext.shouldPassIssuance])

  useEffect(() => {
    const getOffer = async () => {
      await offersContext.getOfferById(OfferId, '/insurance/issuance')
    }

    if (OfferId != undefined && offersContext.plans && offersContext.plans.items.length > 0) {
      getOffer()
    }
  }, [OfferId, offersContext.plans])

  useEffect(() => {
    const getPlans = async () => {
      await offersContext.loadPlans()
    }

    if (offersContext.plans.items.length === 0) {
      getPlans()
    }
  }, [])

  const onContinue = () => {
    let objectsWithFlags = insuredPersonsWithFlags.concat(insuredPersonsVehiclesWithFlags).concat(userPhoneNumberFlag)
    awaitWithLoading(
      new Promise((resolve, reject) => {
        Promise.all(objectsWithFlags.map(q => q.flag.get(100000)))
          .then(_ => resolve())
          .catch(err => reject(err))
        EventEmitterService.dispatch('issuance_save_event')
      })
        .then(_ => {
          objectsWithFlags.forEach(q => {
            q.flag.reset()
          })
          let changed = { ...offersContext.offerCart }
          offersContext.dispatch(
            new Action('CART_CHANGED', {
              newOffer: changed,
              isNewFlow: sessionContext.newPaymentFlow,
              administrativeDivision: sessionContext.user.administrativeDivision,
            })
          )
          EventEmitterService.dispatch(EventKey.FLOW_ISSUANCE_FINISHED)
        })
        .catch(err => {
          objectsWithFlags.forEach(q => {
            q.flag.reset()
          })
          if (err && err.data && err.data != 'validation') {
            EventEmitterService.dispatch(EventKey.ShowError, err)
          }
        })
    )
  }

  return (
    <>
      <Container>
        <FlexView vAlignContent={'center'}>
          <IssuanceSvg className="margin-right-6 medium-icon" />
          <div className="text-title bold blue">Issuance</div>
        </FlexView>
        <Divider />
        <Grid container spacing={3}>
          {insuredPersonsWithFlags.map((user, index) => (
            <Grid key={index} item xs={12} md={6}>
              <div className="text-small black margin-bottom-basic">
                <span className="bold text-body">Rider's information</span> ({user.first_name} {user.last_name})
              </div>
              <IssuanceDriverForm
                index={index}
                DISPATCHED_EVENT={'user_save'}
                defaultValues={user}
                SAVE_EVENT_KEY={'issuance_save_event'}
                flag={user.flag}
              />
            </Grid>
          ))}
        </Grid>
        <Divider />
        {insuredPersonsVehiclesWithFlags.map((vehicle, index) => (
          <div className="margin-bottom-basic margin-top-basic" key={index}>
            <div className="text-small black margin-bottom-basic margin-top-small">
              <FlexView vAlignContent={'center'} className="text-body">
                <div className="bold">Motorcycle VIN</div>
                <SkyInformationPopover style={{ fontSize: '0.9rem' }}>
                  <div className="text-small">{VIN_EXPLANATIONS}</div>
                </SkyInformationPopover>
                <div className="margin-left-3">
                  {' '}
                  ({vehicle.make} {vehicle.model} {vehicle.year}){' '}
                </div>{' '}
              </FlexView>{' '}
            </div>
            <MotorcycleIssuanceForm
              index={index}
              DISPATCHED_EVENT={'vehicle_save'}
              SAVE_EVENT_KEY={'issuance_save_event'}
              defaultValues={vehicle}
              flag={vehicle.flag}
            />
          </div>
        ))}
        <Divider />
        <Grid item sm={12} md={6}>
          <PhoneNumberForm
            DISPATCHED_EVENT={'user_save'}
            defaultValues={sessionContext.user}
            SAVE_EVENT_KEY={'issuance_save_event'}
            flag={userPhoneNumberFlag.flag}
          />
        </Grid>
        <br />
        <FlexView className="margin-top-basic">
          <BackButton />
          {isMobile && <ContinueButton onClick={onContinue} />}
        </FlexView>
      </Container>
    </>
  )
}
